// src/services/pftSurvey.js
//
// PFT Uploads → "text the health survey" (flag PFT_SURVEY_TEXT_ENABLED): which cards
// can be texted, the survey boxes, and what "Text filtered" sends. Firebase-free and
// tested (pftSurvey.test.js). The server re-checks the same rule before sending
// (functions/pftSurvey.js textBlockReason).
//
// A card's survey state comes from its imports' `survey` field, written only by the
// sendPftSurveySms function (and, once answered, by the survey's submit function):
//   { status: "texted" | "responded", recipientId, firstSentAt, lastSentAt, lastSentBy,
//     sendCount, submissionId?, respondedAt? }

import { testDateKey } from "./pftTests";

/** US mobile → +1XXXXXXXXXX, else "" (same rule as the referral texts). */
export const normalizeMobile = (raw) => {
  const d = String(raw == null ? "" : raw).replace(/\D/g, "");
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d[0] === "1") return `+${d}`;
  return "";
};

/** The card's survey record: "responded" beats "texted"; else the latest send. */
export const surveyOf = (imports = []) => {
  let best = null;
  for (const imp of imports) {
    const s = imp && imp.survey;
    if (!s || !s.recipientId) continue;
    if (!best) best = s;
    else if (s.status === "responded" && best.status !== "responded") best = s;
    else if (s.status === best.status && String(s.lastSentAt || "") > String(best.lastSentAt || "")) best = s;
  }
  return best;
};

/** "none" | "texted" | "responded". */
export const surveyStateOf = (card) => {
  const s = card && card.survey;
  if (!s || !s.recipientId) return "none";
  return s.status === "responded" ? "responded" : "texted";
};

// The survey boxes. "Texted" is everyone texted, answered or not.
export const SURVEY_BOXES = [
  { key: "all", label: "All" },
  { key: "notTexted", label: "Not texted" },
  { key: "texted", label: "Texted" },
  { key: "responded", label: "Responded" },
  { key: "waiting", label: "Texted, no response" },
];

/**
 * The Move button's words while a survey box narrows it, so it never says "Move all"
 * when only some patients move (asked 2026-10-26). null for "All" (words as before).
 * scope: undefined (Move all) | "ready" | "filtered", as PftMoveAll's.
 */
export const moveWordingForBox = (box, scope) => {
  const b = SURVEY_BOXES.find((x) => x.key === box);
  if (!b || box === "all") return null;
  const w = b.label.charAt(0).toLowerCase() + b.label.slice(1);
  const button = scope === "ready" ? `Move ready (${w})` : scope === "filtered" ? `Move filtered (${w})` : `Move ${w}`;
  return { button, title: `Move patients in "${b.label}" to Lung Center Referrals` };
};

export const cardInSurveyBox = (card, box = "all") => {
  const s = surveyStateOf(card);
  if (box === "notTexted") return s === "none";
  if (box === "texted") return s !== "none";
  if (box === "responded") return s === "responded";
  if (box === "waiting") return s === "texted";
  return true;
};

// Review items that stop a text (decided 2026-10-23): the name or phone might be
// wrong, so the text could greet the wrong person or reach the wrong phone. "Same
// patient?" (identity) is about the name too. Any other item doesn't block.
export const TEXT_BLOCKING_FIELDS = new Set(["name", "phone", "identity"]);

/**
 * Why this card can't be texted now, or "" when it can. `resend`: the card's own
 * "Text again" button (a card already texted can be texted again, never one that
 * answered).
 */
export const textBlockReason = (card, { resend = false } = {}) => {
  if (!card || card.status !== "new") return "already moved or dismissed";
  const state = surveyStateOf(card);
  if (state === "responded") return "already answered the survey";
  if (state === "texted" && !resend) return "already texted";
  if (!normalizeMobile(card.phone)) return String(card.phone || "").trim() ? "phone isn't a valid mobile number" : "no phone number";
  if (!String(card.name || "").trim()) return "name or phone needs review";
  const pending = new Set((card.pendingImports || []).map((i) => i.id));
  const open = (card.review || []).some(
    (r) => !r.resolved && TEXT_BLOCKING_FIELDS.has(r.field) && (r.importId === null || pending.has(r.importId)),
  );
  return open ? "name or phone needs review" : "";
};

/**
 * What "Text filtered" does with the matching cards:
 *   send      can be texted and still ticked
 *   texted    already texted (each card's own "Text again" can resend)
 *   responded already answered
 *   blocked   [{ card, reason }]: no phone, or name/phone still to check
 *   leftOut   unticked by staff
 */
export const planTextBatch = (cards = [], untickedKeys = new Set()) => {
  const out = { send: [], texted: [], responded: [], blocked: [], leftOut: [] };
  for (const c of cards) {
    if (!c || c.status !== "new") continue;
    const state = surveyStateOf(c);
    if (state === "responded") out.responded.push(c);
    else if (state === "texted") out.texted.push(c);
    else {
      const reason = textBlockReason(c);
      if (reason) out.blocked.push({ card: c, reason });
      else if (untickedKeys.has(c.key)) out.leftOut.push(c);
      else out.send.push(c);
    }
  }
  return out;
};

/** The confirmation's plain-words summary of a batch. */
export const textBatchSummary = (plan) => {
  const why = {};
  for (const b of plan.blocked) why[b.reason] = (why[b.reason] || 0) + 1;
  return [
    `Text ${plan.send.length} patient(s) the health survey link?`,
    plan.texted.length ? `${plan.texted.length} already texted won't be texted again.` : "",
    plan.responded.length ? `${plan.responded.length} already answered.` : "",
    ...Object.entries(why).map(([reason, n]) => `${n} can't be texted: ${reason}.`),
    plan.leftOut.length ? `${plan.leftOut.length} you unticked won't be texted.` : "",
  ]
    .filter(Boolean)
    .join("\n");
};

/** What the page sends the server for one card. */
export const textRequestFor = (card) => ({
  key: card.key,
  importIds: (card.pendingImports || []).map((i) => i.id),
  phone: card.phone || "",
  name: card.name || "",
  pftDate: testDateKey((card.tests || [])[0]) || "",
});

/** "Oct 23, 2026, 2:05 PM" for a stored ISO time; "" if none. */
export const formatSentAt = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};
